import type { Ref } from 'vue'

export interface IRatingCriteria {
  id: number
  name: string
  description?: string
  weight?: number
  display_order?: number
}

export interface IRatingCriteriaSet {
  id: number
  name: string
  rateable_type: string
  status: 'active' | 'inactive'
  criteria: IRatingCriteria[]
  created_at?: string
  updated_at?: string
}

export interface IRatingCriteriaSetsResponse {
  data: IRatingCriteriaSet[]
  links?: {
    prev: string | null
    next: string | null
  }
  meta?: {
    current_page: number
    last_page: number
    per_page: number
    total: number
  }
}

export interface IRatingCriteriaSetResponse {
  data: IRatingCriteriaSet
}

export interface IRatingValue {
  criteria_id: number
  value: number
  criteria?: IRatingCriteria
}

export interface IIndividualRating {
  id: number
  comment?: string
  created_at: string
  updated_at: string
  user?: {
    id: number
    name: string
  }
  values: IRatingValue[]
}

export interface IRatingStats {
  total_ratings: number
  overall_average: number
  criteria_averages: Record<number, number>
  criteria_counts?: Record<number, number>
  star_distribution?: Record<number, number>
}

export interface IRatingStatsResponse {
  data: IRatingStats
}

export interface IRatingsListResponse {
  data: IIndividualRating[]
  links?: {
    prev: string | null
    next: string | null
  }
  meta?: {
    current_page: number
    last_page: number
    per_page: number
    total: number
  }
}

/**
 * Laravel returns validation problems as an `errors` object and everything else
 * as `message`; ofetch nests either under `_data` or `data`.
 */
export const extractRatingErrorMessage = (error: unknown, fallback: string): string => {
  if (error && typeof error === 'object') {
    const err = error as Record<string, unknown>

    for (const key of ['_data', 'data'] as const) {
      const payload = err[key]
      if (!payload || typeof payload !== 'object') continue

      const body = payload as Record<string, unknown>
      if (typeof body.message === 'string') return body.message

      if (body.errors && typeof body.errors === 'object') {
        const messages = Object.values(body.errors as Record<string, unknown>)
          .flatMap((value) => (Array.isArray(value) ? value : [value]))
          .filter((value): value is string => typeof value === 'string')

        if (messages.length) return messages.join('. ')
      }
    }

    if (typeof err.message === 'string') return err.message
  }

  return fallback
}

export const useRating = () => {
  const getRatingCriteriaSets = (params?: {
    rateable_type?: string
    status?: 'active' | 'inactive'
    page?: number
    per_page?: number
    sort?: string
    order?: 'asc' | 'desc'
  }) => {
    const queryParams = new URLSearchParams()

    if (params?.rateable_type) queryParams.set('filter[rateable_type]', params.rateable_type)
    if (params?.status) queryParams.set('filter[status]', params.status)
    if (params?.page) queryParams.set('page', params.page.toString())
    if (params?.per_page) queryParams.set('per_page', params.per_page.toString())
    if (params?.sort) queryParams.set('sort', params.sort)
    if (params?.order) queryParams.set('order', params.order)

    const url = queryParams.toString()
      ? `/rating-criteria-sets?${queryParams.toString()}`
      : '/rating-criteria-sets'

    return useApi<IRatingCriteriaSetsResponse>(url, {
      key: `rating-criteria-sets-${JSON.stringify(params || {})}`
    })
  }

  /**
   * Reactive variant for pages where rateable_type changes after setup.
   * useApi cannot be used: it calls toValue(url) and freezes the request at its
   * initial value. useFetchLocale forwards the getter but bypasses $api, hence
   * the explicit baseApiUrl. The key must stay reactive or Nuxt's payload cache
   * serves the first rateable_type's response for every subsequent one.
   */
  const getRatingCriteriaSetsReactive = (rateableType: Ref<string | null>) => {
    const config = useRuntimeConfig()

    const url = computed(() => {
      if (!rateableType.value) return null

      const queryParams = new URLSearchParams({
        'filter[rateable_type]': rateableType.value,
        'filter[status]': 'active'
      })

      return `${config.public.baseApiUrl}/rating-criteria-sets?${queryParams.toString()}`
    })

    return useFetchLocale<IRatingCriteriaSetsResponse>(url, {
      key: computed(() => `rating-criteria-sets-reactive-${rateableType.value ?? 'none'}`),
      // `watch` only reacts to a change, so a prefilled link would never fetch.
      immediate: !!rateableType.value,
      watch: [url]
    })
  }

  const getRatingCriteriaSet = (criteriaSetId: number | string) => {
    return useApi<IRatingCriteriaSetResponse>(`/rating-criteria-sets/${criteriaSetId}`, {
      key: `rating-criteria-set-${criteriaSetId}`
    })
  }

  const getRatingStats = (rateableType: string, rateableId: number) => {
    const queryParams = new URLSearchParams({
      rateable_type: rateableType,
      rateable_id: rateableId.toString()
    })

    return useApi<IRatingStatsResponse>(`/ratings/statistics?${queryParams.toString()}`, {
      key: `rating-stats-${rateableType}-${rateableId}`
    })
  }

  const canRate = (rateableType: string, rateableId: number) => {
    return useApi<{ can_rate: boolean }>(`/ratings/${rateableType}/${rateableId}/can-rate`, {
      key: `can-rate-${rateableType}-${rateableId}`
    })
  }

  const getIndividualRatings = (params: {
    rateable_type: string
    rateable_id: number
    with_comments_only?: boolean
    include?: string[]
    page?: number
    per_page?: number
  }) => {
    const queryParams = new URLSearchParams({
      'filter[rateable_type]': params.rateable_type,
      'filter[rateable_id]': params.rateable_id.toString()
    })

    // if (params.with_comments_only) {
    //   queryParams.set('filter[has_comment]', '1')
    // }

    if (params.include && params.include.length > 0) {
      queryParams.set('include', params.include.join(','))
    }

    if (params.page) {
      queryParams.set('page', params.page.toString())
    }

    if (params.per_page) {
      queryParams.set('per_page', params.per_page.toString())
    }

    return useApi<IRatingsListResponse>(`/ratings?${queryParams.toString()}`, {
      key: `individual-ratings-${JSON.stringify(params)}`
    })
  }

  const submitRating = async (data: {
    rateable_id: number
    rateable_type: string
    criteria_set_id: number
    values: Array<{ criteria_id: number; value: number }>
    comment?: string
  }) => {
    return await useApiPost('/ratings', data)
  }

  return {
    getRatingCriteriaSets,
    getRatingCriteriaSetsReactive,
    getRatingCriteriaSet,
    getRatingStats,
    getIndividualRatings,
    canRate,
    submitRating
  }
}
